// src/app/core/breakpoint.service.ts
import { inject, Injectable, PLATFORM_ID, signal, computed, effect } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BreakpointObserver } from '@angular/cdk/layout';
import { TAILWIND_QUERIES, TailwindSize } from './tailwind-screens';

type MatchesMap = Partial<Record<Exclude<TailwindSize, 'xs'>, boolean>>;

@Injectable({ providedIn: 'root' })
export class BreakpointService {
  private platformId = inject(PLATFORM_ID);
  private observer = inject(BreakpointObserver);

  // Internal: which min-width queries are currently true
  private _matches = signal<MatchesMap>({
    sm: false, md: false, lg: false, xl: false, '2xl': false,
  });

  // Public: current size bucket. Tailwind logic: highest min-width that matches wins.
  current = computed<TailwindSize>(() => {
    const m = this._matches();
    if (m['2xl']) return '2xl';
    if (m['xl'])  return 'xl';
    if (m['lg'])  return 'lg';
    if (m['md'])  return 'md';
    if (m['sm'])  return 'sm';
    return 'xs'; // < sm
  });

  // Convenience flags you can use in components/templates
  isXs     = computed(() => this.current() === 'xs');
  isSmUp   = computed(() => this.current() !== 'xs');
  isMdUp   = computed(() => ['md','lg','xl','2xl'].includes(this.current()));
  isLgUp   = computed(() => ['lg','xl','2xl'].includes(this.current()));
  isXlUp   = computed(() => ['xl','2xl'].includes(this.current()));
  is2xlUp  = computed(() => this.current() === '2xl');

  isBelowSm = computed(() => this.current() === 'xs');
  isBelowMd = computed(() => ['xs','sm'].includes(this.current()));
  isBelowLg = computed(() => ['xs','sm','md'].includes(this.current()));
  isBelowXl = computed(() => ['xs','sm','md','lg'].includes(this.current()));

  constructor() {
    if (!isPlatformBrowser(this.platformId)) return;

    const queries = Object.values(TAILWIND_QUERIES);
    this.observer.observe(queries).subscribe(result => {
      // Update map with each query’s state
      const next: MatchesMap = {};
      (Object.keys(TAILWIND_QUERIES) as Array<Exclude<TailwindSize,'xs'>>).forEach((key) => {
        const q = TAILWIND_QUERIES[key];
        next[key] = !!result.breakpoints[q];
      });
      this._matches.set(next);
    });

    // Optional: log changes while you wire things up
    effect(() => {
      // console.log('[Breakpoint]', this.current());
    });
  }
}
